import type { ReactNode } from 'react';
import { Alert, CircularProgress, Stack } from '@mui/material';
import { AppButton } from '../design-system';

export interface GatewayConfigPushCommandsProps {
  hasPushOverrides: boolean;
  pushOverridesMessage?: string;
  noPushOverridesMessage?: string;
  previewLabel?: string;
  pushLabel?: string;
  previewDisabled?: boolean;
  pushDisabled?: boolean;
  previewLoading?: boolean;
  pushPending?: boolean;
  onPreview: () => void;
  onPush: () => void;
  pushError?: string | null;
  pushSuccess?: ReactNode;
  onDismissPushResult?: () => void;
}

export function GatewayConfigPushCommands({
  hasPushOverrides,
  pushOverridesMessage = 'Active push-time overrides are configured (Layer 4). They will be merged during this push only.',
  noPushOverridesMessage = 'No push-time overrides configured. Persisted overrides (layers 1–3) will be deployed.',
  previewLabel = 'Preview',
  pushLabel = 'Push Config',
  previewDisabled = false,
  pushDisabled = false,
  previewLoading = false,
  pushPending = false,
  onPreview,
  onPush,
  pushError = null,
  pushSuccess = null,
  onDismissPushResult,
}: GatewayConfigPushCommandsProps) {
  return (
    <>
      {hasPushOverrides ? (
        <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
          {pushOverridesMessage}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          {noPushOverridesMessage}
        </Alert>
      )}
      {pushSuccess}
      {pushError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={onDismissPushResult}>
          {pushError}
        </Alert>
      ) : null}
      <Stack direction="row" spacing={2} alignItems="center">
        <AppButton
          variant="outlined"
          size="small"
          onClick={onPreview}
          disabled={previewDisabled || previewLoading}
          sx={{ textTransform: 'none' }}
        >
          {previewLoading ? <CircularProgress size={16} /> : previewLabel}
        </AppButton>
        <AppButton
          variant="contained"
          size="small"
          disabled={pushDisabled || pushPending}
          onClick={onPush}
          sx={{ textTransform: 'none' }}
        >
          {pushPending ? <CircularProgress size={22} color="inherit" /> : pushLabel}
        </AppButton>
      </Stack>
    </>
  );
}
